<template>
    <ChatAgentToolFrame is-actionable>
        <template v-if="action.action_state.submitted">
            <div class="flex flex-col gap-1">
                <div class="nadia-chat-bold text-ink">{{ t("chat.emailSummary.sentTitle") }}</div>
                <div class="body-small text-ink-muted">{{ t("chat.emailSummary.sentFollowup") }}</div>
            </div>
            <BaseButton theme="primary" @click="handleWrapUp">{{ t("chat.emailSummary.wrapUp") }}</BaseButton>
        </template>
        <template v-else>
            <div class="flex flex-col gap-4">
                <h3 class="nadia-chat-bold text-center text-ink">{{ t("chat.emailSummary.title") }}</h3>
                <BaseTextarea
                    v-if="editing"
                    ref="summaryInput"
                    v-model:input="content"
                    rows="5"
                    class="rounded-lg border border-control-border bg-control-bg p-3 text-control-text focus-field"
                    :disabled="loading"
                    :aria-label="t('chat.emailSummary.summaryAria')" />
                <div v-else class="body-small whitespace-pre-line text-ink">{{ renderedSummary }}</div>
                <BaseButton v-if="!editing && needsTruncating" theme="neutral" class="flex w-full items-center justify-center" @click="expanded = !expanded">{{
                    expanded ? t("chat.emailSummary.seeLess") : t("chat.emailSummary.seeMore")
                }}</BaseButton>
                <BaseButton theme="neutral" class="flex w-full items-center justify-center" :disabled="loading" @click="toggleEdit">
                    {{ editing ? t("chat.emailSummary.done") : t("chat.emailSummary.edit") }}
                </BaseButton>
                <BaseNotification v-if="hasError" theme="error" hide-dismiss :text="t('chat.emailSummary.errorText')" />
                <BaseButton v-if="!hideEmailButton" :disabled="loading" theme="primary" class="flex w-full items-center justify-center gap-1" @click="handleSubmit">
                    <LoadingSpinner v-if="loading" class="h-4" />{{ t("chat.emailSummary.sendLabel") }}
                </BaseButton>
            </div>
        </template>
    </ChatAgentToolFrame>
</template>

<script setup>
import { ACTION } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import { CHAT_EVENT } from "~vue/events";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";

const { t } = useI18n();

const TRUNCATE_LENGTH = 200;

const props = defineProps({
    action: {
        type: Object,
        required: true,
    },
    lineIdx: { type: Number, required: true },
    messageId: { type: Number, required: true },
    hideEmailButton: { type: Boolean, default: false },
});

const { emitter, $sendEvent } = inject("globalProperties");
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();
const { execute: executeAction } = useActionHandler();

const hasError = ref(false);
const content = ref(props.action.action_params.summary);
const editing = ref(false);
const summaryInput = useTemplateRef("summaryInput");

const needsTruncating = computed(() => content.value.length > TRUNCATE_LENGTH);
const expanded = ref(!needsTruncating.value);

const renderedSummary = computed(() => {
    if (!expanded.value && needsTruncating.value) {
        const base = content.value.substring(0, TRUNCATE_LENGTH);
        return base.substring(0, base.lastIndexOf(" "));
    }

    return content.value;
});

async function toggleEdit() {
    editing.value = !editing.value;
    if (editing.value) {
        logUserInteraction("edit_email_summary");
        await nextTick();
        summaryInput.value?.textareaRef?.focus();
    }
}

async function handleSubmit() {
    startLoading();
    hasError.value = false;

    const payload = {
        message_id: props.messageId,
        lineIdx: props.lineIdx,
        action_params: {
            summary: content.value,
        },
        action_state: {
            submitted: true,
        },
    };

    try {
        await $sendEvent(CHAT_EVENT.EMAIL_CHAT_SUMMARY, { content: content.value });
        await executeAction(ACTION.EMAIL_CHAT_SUMMARY, payload, { sync: true });
    } catch (e) {
        logError(e);
        hasError.value = true;
    } finally {
        stopLoading();
    }
}

function handleWrapUp() {
    emitter.emit(CHAT_EVENT.WRAP_UP_CHAT);
}
</script>
